import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { StoryMode } from "@/features/story";
import { cn } from "@/lib/utils";

type MainStorySetupCardProps = {
  busy?: boolean;
  onCreate: (mode: StoryMode) => Promise<void>;
};

export function MainStorySetupCard({
  busy = false,
  onCreate,
}: MainStorySetupCardProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<StoryMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!mode || busy) {
      return;
    }

    try {
      setError(null);
      await onCreate(mode);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Story setup failed.");
    }
  }

  return (
    <div className="border border-border rounded-lg bg-card">
      <div className="space-y-6 px-6 py-8">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("story.setup.eyebrow")}
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">{t("story.setup.title")}</h2>
          <p className="max-w-3xl leading-7 text-muted-foreground">
            {t("story.setup.body")}
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {(["chapters", "continuous"] as const).map((option) => (
            <button
              key={option}
              className={cn(
                "rounded-md border px-5 py-5 text-left transition-colors",
                mode === option
                  ? "border-primary/20 bg-accent text-accent-foreground"
                  : "border-transparent bg-muted/50 hover:bg-muted text-muted-foreground",
              )}
              onClick={() => setMode(option)}
              type="button"
            >
              <p className={cn("text-base font-semibold", mode === option ? "text-foreground" : "text-foreground/80")}>
                {t(`story.modes.${option}`)}
              </p>
              <p className="mt-2 text-sm leading-6">
                {option === "chapters"
                  ? t("story.setup.chaptersDescription")
                  : t("story.setup.continuousDescription")}
              </p>
            </button>
          ))}
        </div>

        {error ? (
          <p className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <Button disabled={!mode || busy} onClick={() => void handleCreate()} type="button">
          {busy ? t("story.setup.creating") : t("story.setup.createAction")}
        </Button>
      </div>
    </div>
  );
}
